/**
 * GithubAppSection: whether the TrainLoop GitHub App is installed for
 * this team, where, and which repositories it can reach (Settings >
 * GitHub App). The Review page's eval PRs are opened through that
 * installation, so a team with Review gets this section with the other
 * Jobs sections.
 *
 * The API asks GitHub on every load and answers with a status, never an
 * error, so each state renders its own next step: install the App and
 * send TrainLoop the installation id, install it again, or try again
 * later. Nothing here writes. Linking an installation to the team is a
 * staff action (docs/github-app-runbook.md), so under impersonation the
 * page reads the same as it does for the team.
 */

import { Check, ExternalLink, RefreshCw } from 'lucide-react'

import { getApiErrorDetail } from '@/api/client'
import type {
  GithubAppInstallation,
  GithubAppRepository,
  GithubAppStatus,
} from '@/api/generated/model'
import type { SettingsSectionProps } from '@/components/settings/sections'
import { SettingsList, SettingsListRow } from '@/components/settings/SettingsList'
import { SettingsPageHeader } from '@/components/settings/SettingsPageHeader'
import { SettingsBlock, SettingsSection } from '@/components/settings/SettingsSection'
import { GithubAppStatusBadge } from '@/components/shared/GithubAppStatusBadge'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EYEBROW } from '@/components/ui/eyebrow'
import { Skeleton } from '@/components/ui/skeleton'
import { useGithubAppStatus } from '@/hooks/useTeamSettings'

const SUPPORT_EMAIL = 'support@trainloop.ai'

const EXTERNAL_LINK =
  'inline-flex items-center gap-1 text-foreground underline underline-offset-4 hover:text-primary'

/**
 * The API fills these fields for the statuses that render them; a hole
 * here is a contract break, not a state to paper over.
 */
function required<T>(value: T | null, what: string): T {
  if (value === null) throw new Error(`GitHub App status is missing ${what}`)
  return value
}

export function GithubAppSection({ section }: SettingsSectionProps): React.JSX.Element {
  const statusQuery = useGithubAppStatus()

  return (
    <div>
      <SettingsPageHeader
        section={section}
        badge={statusQuery.data ? <GithubAppStatusBadge status={statusQuery.data.status} /> : undefined}
        action={
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={statusQuery.isFetching}
            onClick={statusQuery.refetch}
          >
            <RefreshCw aria-hidden="true" />
            Check again
          </Button>
        }
      />

      {statusQuery.isLoading ? (
        <SettingsBlock>
          <Skeleton className="h-24 w-full" />
        </SettingsBlock>
      ) : statusQuery.isError ? (
        <SettingsBlock>
          <p className="text-sm text-destructive" role="alert">
            {getApiErrorDetail(statusQuery.error) ?? 'The installation could not be checked.'}
          </p>
        </SettingsBlock>
      ) : statusQuery.data ? (
        <StatusBody status={statusQuery.data} />
      ) : null}
    </div>
  )
}

function StatusBody({ status }: { status: GithubAppStatus }): React.JSX.Element {
  switch (status.status) {
    case 'installed':
      return <Installed status={status} installation={required(status.installation, 'installation')} />
    case 'not_connected':
      return (
        <SettingsSection
          title="Install the App"
          description="TrainLoop opens eval PRs and reads your code through a GitHub App installed on your own organization. You can remove it from GitHub at any time."
        >
          <InstallSteps installUrl={required(status.install_url, 'install_url')} evalPrRepo={status.eval_pr_repo} />
        </SettingsSection>
      )
    case 'uninstalled':
      return (
        <SettingsSection
          title="Install the App again"
          description="The installation linked to this team is gone from GitHub, so eval PRs cannot be opened until a new one is linked."
        >
          <p className="mb-6 max-w-xl text-sm text-destructive" role="alert">
            {required(status.detail, 'detail')}
          </p>
          <InstallSteps installUrl={required(status.install_url, 'install_url')} evalPrRepo={status.eval_pr_repo} />
        </SettingsSection>
      )
    case 'unavailable':
      return (
        <SettingsBlock>
          <p className="text-sm text-muted-foreground" role="alert">
            {required(status.detail, 'detail')}
          </p>
        </SettingsBlock>
      )
  }
}

/** The customer's side of onboarding: install, then tell TrainLoop the id. */
function InstallSteps({
  installUrl,
  evalPrRepo,
}: {
  installUrl: string
  evalPrRepo: string | null
}): React.JSX.Element {
  return (
    <ol className="max-w-xl space-y-5 text-sm text-foreground">
      <Step number={1} title="Install the App on GitHub">
        <p>
          Choose the organization that owns your repositories and pick Only select repositories.
          {evalPrRepo !== null && (
            <>
              {' '}
              Include <code className="font-mono">{evalPrRepo}</code>, where eval PRs are opened.
            </>
          )}
        </p>
        <Button asChild size="sm" className="mt-3">
          <a href={installUrl} target="_blank" rel="noreferrer">
            Install on GitHub
            <ExternalLink aria-hidden="true" />
          </a>
        </Button>
      </Step>
      <Step number={2} title="Send TrainLoop the installation id">
        <p>
          After the install, GitHub opens the installation page. The number at the end of its
          address is the installation id. Email it to{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} className={EXTERNAL_LINK}>
            {SUPPORT_EMAIL}
          </a>{' '}
          and TrainLoop links it to this team.
        </p>
      </Step>
      <Step number={3} title="Check again">
        <p>Once the installation is linked, this page shows it and the repositories it covers.</p>
      </Step>
    </ol>
  )
}

function Step({
  number,
  title,
  children,
}: {
  number: number
  title: string
  children: React.ReactNode
}): React.JSX.Element {
  return (
    <li className="flex gap-4">
      <span className="w-5 shrink-0 pt-px font-mono text-xs text-muted-foreground">{number}</span>
      <div className="min-w-0 space-y-1">
        <p className="font-medium text-foreground">{title}</p>
        <div className="text-muted-foreground">{children}</div>
      </div>
    </li>
  )
}

function Installed({
  status,
  installation,
}: {
  status: GithubAppStatus
  installation: GithubAppInstallation
}): React.JSX.Element {
  const evalPrRepo = status.eval_pr_repo
  const shown = installation.repositories.length
  return (
    <>
      <SettingsSection
        title="Installation"
        description="See where the App is installed. Add or remove repositories on the installation page on GitHub."
      >
        <dl className="max-w-xl space-y-3 text-sm">
          <Fact label="Account">
            <span className="font-mono">{installation.account}</span>
          </Fact>
          <Fact label="Installation id">
            <span className="font-mono">{installation.installation_id}</span>
          </Fact>
          <Fact label="Manage">
            <a href={installation.html_url} target="_blank" rel="noreferrer" className={EXTERNAL_LINK}>
              Installation page on GitHub
              <ExternalLink className="h-3 w-3" aria-hidden="true" />
            </a>
          </Fact>
        </dl>
        {evalPrRepo !== null && (
          <EvalPrRepoNote repo={evalPrRepo} covered={status.eval_pr_repo_covered} />
        )}
      </SettingsSection>

      <SettingsSection
        title="Repositories"
        description={
          installation.repository_selection === 'all'
            ? `The App can reach every repository on ${installation.account} and any repository created later.`
            : 'The App can read these repositories and open pull requests in them.'
        }
      >
        {shown === 0 ? (
          <p className="text-sm text-muted-foreground">
            No repositories are selected. Add one on the installation page on GitHub.
          </p>
        ) : (
          <SettingsList>
            {installation.repositories.map((repo) => (
              <RepositoryRow key={repo.full_name} repo={repo} isEvalPrRepo={sameRepo(repo.full_name, evalPrRepo)} />
            ))}
          </SettingsList>
        )}
        {installation.repository_count > shown && (
          <p className="mt-3 text-xs text-muted-foreground">
            This list shows {shown} of {installation.repository_count} repositories.
          </p>
        )}
      </SettingsSection>
    </>
  )
}

function Fact({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="grid grid-cols-[8rem_minmax(0,1fr)] gap-3">
      <dt className={EYEBROW}>{label}</dt>
      <dd className="min-w-0 truncate text-foreground">{children}</dd>
    </div>
  )
}

/** GitHub repository names are case-insensitive. */
function sameRepo(fullName: string, repo: string | null): boolean {
  return repo !== null && fullName.toLowerCase() === repo.toLowerCase()
}

function EvalPrRepoNote({
  repo,
  covered,
}: {
  repo: string
  covered: boolean | null
}): React.JSX.Element | null {
  if (covered === true) {
    return (
      <p className="mt-4 flex items-start gap-2 text-sm text-foreground">
        <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden="true" />
        <span>
          Eval PRs are opened in <code className="font-mono">{repo}</code>.
        </span>
      </p>
    )
  }
  if (covered === false) {
    return (
      <p className="mt-4 max-w-xl text-sm text-destructive" role="alert">
        Eval PRs are set to open in <code className="font-mono">{repo}</code>, which the App cannot
        reach. Add the repository on the installation page on GitHub.
      </p>
    )
  }
  return null
}

function RepositoryRow({
  repo,
  isEvalPrRepo,
}: {
  repo: GithubAppRepository
  isEvalPrRepo: boolean
}): React.JSX.Element {
  return (
    <SettingsListRow>
      <span className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
        <a
          href={repo.html_url}
          target="_blank"
          rel="noreferrer"
          className="truncate font-mono text-sm text-foreground underline-offset-4 hover:underline"
        >
          {repo.full_name}
        </a>
        {repo.private && <Badge variant="outline">Private</Badge>}
        {isEvalPrRepo && <Badge variant="success">Eval PRs</Badge>}
      </span>
    </SettingsListRow>
  )
}
